import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useFormContext, useWatch } from '@astro/forms'
import { TextInput, RadioGroup, getErrorMessage } from '../fields'
import SelectField from '../SelectField'
import ProfilePhotoField from '../ProfilePhotoField'
import { FALLBACK_STATES, toList } from '../registerSteps'

/* Backend slugs with display labels. */
const CONSULTATION_MODES = [
  { value: 'video', label: 'Video Call' },
  { value: 'call', label: 'Audio Call' },
  { value: 'chat', label: 'Chat' },
]

/** ~150 words minimum, per the design brief on the bio field. */
const MIN_BIO_WORDS = 150

const countWords = (text) => (text ?? '').trim().split(/\s+/).filter(Boolean).length

/** `minWords=0` (profile edit) keeps the bio required but drops the word-count
    gate — stored bios must stay saveable regardless of length. */
function BioField({ minWords = MIN_BIO_WORDS }) {
  const { register, formState: { errors } } = useFormContext()
  const error = getErrorMessage(errors, 'details_bio')
  const words = countWords(useWatch({ name: 'details_bio' }))
  const enough = words >= minWords
  return (
    <div>
      <div className="flex items-center justify-between mb-1.75">
        <label className="ar-label mb-0">
          Detailed Bio
          {minWords > 0 && <span className="normal-case font-normal text-stone-400"> (min. {minWords} words)</span>}
        </label>
        {minWords > 0 && (
          <span
            className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap ${
              enough ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
            }`}
          >
            {words} / {minWords} words
          </span>
        )}
      </div>
      <textarea
        placeholder="Describe your journey, specializations and approach to consultations..."
        {...register('details_bio', {
          required: 'A detailed bio is required.',
          validate: (v) =>
            minWords <= 0 || countWords(v) >= minWords || `Bio should be at least ${minWords} words.`,
        })}
        className={`ar-input${error ? ' has-error' : ''}`}
      />
      {error && <p className="ar-error">{error}</p>}
    </div>
  )
}

function StateCityFields() {
  const { setValue } = useFormContext()
  // Selects store lookup IDs (as strings) — the payload wants state_id/city_id.
  const selectedStateId = useWatch({ name: 'state_id' })

  // Reference data — cache for the session, same as the shop address book.
  const statesQuery = useQuery({
    queryKey: ['states'],
    queryFn: () => api.lookups.states(),
    staleTime: Infinity,
    retry: false,
  })
  const states = toList(statesQuery.data)
  const selectedState = states.find((s) => String(s.id) === String(selectedStateId))

  const citiesQuery = useQuery({
    queryKey: ['cities', selectedState?.id],
    queryFn: () => api.lookups.cities(selectedState.id),
    enabled: selectedState != null,
    staleTime: Infinity,
    retry: false,
  })
  const cities = toList(citiesQuery.data)

  const stateOptions = states.length
    ? states.map((s) => ({ value: String(s.id), label: s.name }))
    : FALLBACK_STATES

  return (
    <>
      <SelectField
        name="state_id"
        label="State"
        options={stateOptions}
        placeholder="Select state"
        isLoading={statesQuery.isLoading}
        rules={{ required: 'Select your state.' }}
        onChangeValue={() => setValue('city_id', '')}
      />
      {/* Always the same widget — swapping between register() and Controller
          leaves a stale registration behind and validation reads an empty
          value. Creatable covers states whose city lookup returns nothing. */}
      <SelectField
        name="city_id"
        label="City"
        creatable
        options={cities.map((c) => ({ value: String(c.id), label: c.name }))}
        placeholder={selectedStateId ? 'Select or type city' : 'Select state first'}
        isDisabled={!selectedStateId}
        isLoading={selectedState != null && citiesQuery.isLoading}
        rules={{ required: 'Select your city.' }}
      />
    </>
  )
}

/** Profile-edit variant: `showPhoto=false` hides the upload (the JSON update
    contract carries no files) and `bioMinWords=0` drops the word-count gate. */
export default function StepProfile({ showPhoto = true, bioMinWords = MIN_BIO_WORDS }) {
  return (
    <div className="step-panel space-y-4">
      <TextInput
        name="short_intro"
        label="Short Intro"
        placeholder="One line about your practice, e.g. 15 yrs Vedic Astrologer & Numerologist"
      />
      <BioField minWords={bioMinWords} />
      {showPhoto && <ProfilePhotoField name="profile_photo" />}
      <TextInput
        name="address"
        label="Address"
        placeholder="Street, Area"
        rules={{ required: 'Address is required.' }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StateCityFields />
        <TextInput
          name="pin_code"
          label="Pin Code"
          placeholder="110001"
          maxLength={6}
          digitsOnly
          rules={{
            required: 'Pin code is required.',
            pattern: { value: /^[0-9]{6}$/, message: 'Enter a valid 6-digit pin code.' },
          }}
        />
      </div>
      <RadioGroup
        name="consultation_mode"
        label="Consultation Mode"
        options={CONSULTATION_MODES}
        rules={{ required: 'Select a consultation mode.' }}
      />
    </div>
  )
}
